import { AccommodationListItem } from "../AccommodationsPage/accommodationsSlice";
import { Button, Col, Collapse, Row, Spinner } from "react-bootstrap";
import CabinCard from "../../components/CabinCard";
import { ICalEventModal } from "../../components/ICalEventModal";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  BookingItem,
  BookingsByIdsRequest,
  BookingsRequest,
  BookingType,
  getAccommodationsBookingsAsync,
  getAccommodationsBookingsByIdsAsync,
  getAccommodationsCalendarBookingsBatchAsync,
  getAccommodationsCalendarBookingsAsync,
  getBookingsAsync,
  ReservedDate,
  selectAccommodationsBookingItems,
  selectAccommodationsCalendarBookingItems,
  selectBookingItems,
} from "./bookingsSlice";
import {
  constructCalendarHighlights,
  constructCalendarReservedDates,
  constructCalendarReservedDatesDummy,
  convertJSDateToDotNetDateTimeDayEnd,
  convertJSDateToDotNetDateTimeDayStart,
} from "../../common/misc";
import { useAuthUser } from "../../features/auth/authHooks";
import ReactDatePicker from "react-datepicker";
import { DateRange, DayPicker } from "react-day-picker";
import { unwrapResult } from "@reduxjs/toolkit";
import ReactDayPickerWrapper from "../../components/DayPicker/ReactDayPickerWrapper";
import BookingModalWrapper from "./BookingModalWrapper";
import { Link } from "react-router-dom";
import { Icon } from "@lomarengas/design-system";
import BookingRowsTable from "./BookingRowsTable";
const AccommodationBookings = ({
  accommodation,
  allAccommodationIds = [],
  useBatchCalendarFetch = false,
  isBatchCalendarFetchLeader = false,
}: {
  accommodation: AccommodationListItem;
  allAccommodationIds?: string[];
  useBatchCalendarFetch?: boolean;
  isBatchCalendarFetchLeader?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();
  const { isOwnerUser } = useAuthUser();
  const [pickerShowDate, setPickerShowDate] = useState<Date>();
  const [selected, setSelected] = useState<Date>();
  const [currentFetchDate, setCurrentFetchDate] = useState<Date>(new Date());
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>();
  const [bookingDetailsTableOpen, setBookingDetailsTableOpen] = useState(false);
  const [showICalModal, setShowICalModal] = useState(false);
  const [selectedICalBooking, setSelectedICalBooking] =
    useState<ReservedDate | null>(null);
  const hasHandledInitialBatchMode = useRef(false);
  const allAccommodationIdsKey = (allAccommodationIds ?? []).join("|");
  const bookingsForCalendar = useAppSelector((state) =>
    selectAccommodationsCalendarBookingItems(state, accommodation.id),
  );
  const fullBookingDetailsForBookingsTable = useAppSelector((state) =>
    selectAccommodationsBookingItems(state, accommodation.id),
  );

  const handleIcalClick = (reservedDate: ReservedDate) => {
    setSelectedICalBooking(reservedDate);
    setShowICalModal(true);
  };

  //let bookings = useAppSelector(selectAccommodationsBookingItems(accommodationId: accommodation.id));

  const initFetch = useCallback(
    (date: Date) => {
      //Set date to first of the fetched month.
      let startFetchDate = new Date(date);
      startFetchDate.setDate(1);
      let calendarStartFetchDate = new Date(startFetchDate);
      //Set enddate as 1 of the month 3 months from now
      let calendarEndDate = new Date(
        startFetchDate.setMonth(startFetchDate.getMonth() + 3),
      );

      let bookingsRequest: BookingsRequest = new (class
        implements BookingsRequest
      {
        accommodationId: string;
        ownerId: string;
        isOwnerUser: boolean;
        bookingsStartDate: string;
        bookingsEndDate: string;
      })();
      bookingsRequest.accommodationId = accommodation.id;
      bookingsRequest.ownerId = ownerAccountId;
      bookingsRequest.isOwnerUser = isOwnerUser;
      bookingsRequest.bookingsStartDate = convertJSDateToDotNetDateTimeDayStart(
        calendarStartFetchDate,
      );
      bookingsRequest.bookingsEndDate =
        convertJSDateToDotNetDateTimeDayEnd(calendarEndDate);
      dispatch(getAccommodationsCalendarBookingsAsync(bookingsRequest));
    },
    [accommodation.id, dispatch, isOwnerUser, ownerAccountId],
  );
  const [isFullBookingDetailsLoading, setIsFullBookingDetailsLoading] =
    useState(false);
  useEffect(() => {
    if (useBatchCalendarFetch && !hasHandledInitialBatchMode.current) {
      hasHandledInitialBatchMode.current = true;

      if (isBatchCalendarFetchLeader) {
        let startFetchDate = new Date(currentFetchDate);
        startFetchDate.setDate(1);
        const calendarStartFetchDate = new Date(startFetchDate);
        const calendarEndDate = new Date(
          startFetchDate.setMonth(startFetchDate.getMonth() + 3),
        );

        const ids =
          allAccommodationIdsKey.length > 0
            ? allAccommodationIdsKey.split("|")
            : [accommodation.id];

        dispatch(
          getAccommodationsCalendarBookingsBatchAsync({
            accommodationIds: ids,
            ownerId: ownerAccountId,
            isOwnerUser: isOwnerUser,
            bookingsStartDate: convertJSDateToDotNetDateTimeDayStart(
              calendarStartFetchDate,
            ),
            bookingsEndDate:
              convertJSDateToDotNetDateTimeDayEnd(calendarEndDate),
          }),
        );
      }

      return;
    }

    // After user changes month in this specific accommodation, use single fetch.
    initFetch(currentFetchDate);
  }, [
    accommodation.id,
    currentFetchDate,
    dispatch,
    initFetch,
    isBatchCalendarFetchLeader,
    isOwnerUser,
    ownerAccountId,
    allAccommodationIdsKey,
    useBatchCalendarFetch,
  ]);

  useEffect(() => {
    if (!bookingDetailsTableOpen) return;

    const ids = Array.from(
      new Set(
        (bookingsForCalendar || [])
          .map((b: any) => b.id)
          .filter((x: string) => !!x),
      ),
    );

    let request: BookingsByIdsRequest = new (class
      implements BookingsByIdsRequest
    {
      accommodationId: string;
      ownerId: string;
      isOwnerUser: boolean;
      bookingIds: string[];
    })();

    request.accommodationId = accommodation.id;
    request.ownerId = ownerAccountId;
    request.isOwnerUser = isOwnerUser;
    request.bookingIds = ids ?? [];

    setIsFullBookingDetailsLoading(true);
    dispatch(getAccommodationsBookingsByIdsAsync(request))
      .unwrap()
      .finally(() => setIsFullBookingDetailsLoading(false));
  }, [bookingDetailsTableOpen, bookingsForCalendar]);

  const refetchAfterICalSave = () => {
    initFetch(currentFetchDate);

    if (!bookingDetailsTableOpen) return;

    const ids = Array.from(
      new Set(
        (bookingsForCalendar || [])
          .map((b: any) => b.id)
          .filter((x: string) => !!x),
      ),
    );

    let request: BookingsByIdsRequest = new (class
      implements BookingsByIdsRequest
    {
      accommodationId: string;
      ownerId: string;
      isOwnerUser: boolean;
      bookingIds: string[];
    })();

    request.accommodationId = accommodation.id;
    request.ownerId = ownerAccountId;
    request.isOwnerUser = isOwnerUser;
    request.bookingIds = ids ?? [];

    setIsFullBookingDetailsLoading(true);
    dispatch(getAccommodationsBookingsByIdsAsync(request))
      .unwrap()
      .finally(() => setIsFullBookingDetailsLoading(false));
  };

  let location = "";
  if (
    accommodation.municipality != null &&
    accommodation.municipality.length > 0
  ) {
    location = accommodation.municipality;
  }
  if (accommodation.region && accommodation.region.length > 0) {
    location += ", " + accommodation.region;
  }
  if (accommodation.skiResort && accommodation.skiResort.length > 0) {
    location += ", " + accommodation.skiResort;
  }
  let description =
    accommodation.beds + " + " + accommodation.extraBeds + " henkilöä";
  let badgeText = "";
  let badgeBg = "";
  if (accommodation.novelty == 1) {
    badgeText = "Uutuus";
    badgeBg = "success";
  }
  function useDeviceMode() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [isPrint, setIsPrint] = useState(false);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 1024);

      const mediaQuery = window.matchMedia("print");

      const handlePrintChange = (e: MediaQueryListEvent) => {
        setIsPrint(e.matches);
      };

      // kuunnellaan resize
      window.addEventListener("resize", handleResize);

      // kuunnellaan print-tila (media queryn muutos)
      mediaQuery.addEventListener("change", handlePrintChange);

      // siivotaan
      return () => {
        window.removeEventListener("resize", handleResize);
        mediaQuery.removeEventListener("change", handlePrintChange);
      };
    }, []);

    return { isMobile, isPrint };
  }

  const { isMobile, isPrint } = useDeviceMode();

  if (bookingsForCalendar === undefined) {
    return (
      <Row key={accommodation.id}>
        <Col lg={3}>
          <CabinCard
            Url={
              "/bookings/cabin/" +
              accommodation.id +
              "?accommodationId=" +
              accommodation.id +
              "&accommodationName=" +
              accommodation.accommodationNumber +
              " - " +
              accommodation.name
            }
            ImgSrc={accommodation.imageCarouselUrl}
            Title={
              accommodation.accommodationNumber + ", " + accommodation.name
            }
            Location={location}
            Desc={description}
            BadgeBg={badgeBg}
            BadgeText={badgeText}
          />
        </Col>
        <Col lg={9}>
          <div className="d-flex gap-16 align-items-center mt-16">
            <Spinner animation="border" />
            <span>Ladataan varauksia</span>
          </div>
        </Col>
      </Row>
    );
  }
  const onMonthChange = (date: Date) => {
    setCurrentFetchDate(date);
  };

  const onDatePickerRangeSelected = (selected: DateRange) => {
    if (selected.from != null && selected.to != null) {
      //TODO: service booking start and endtimes need to get somewhere?
      selected.from.setHours(16);
      selected.to.setHours(12);
      // console.log(selected);
      setDayPickerSelectedRange(selected);
      setBookingModalOpen(true);
    }
    //console.log(triggerDate);
  };
  let highlightWithRangesForBookings =
    constructCalendarHighlights(bookingsForCalendar);
  let reservedDates = constructCalendarReservedDates(bookingsForCalendar);

  //let reservedDates = constructCalendarReservedDatesDummy();
  //console.log(reservedDates);

  return (
    <>
      <Row key={accommodation.id} className="g-24">
        <Col xl={3}>
          <CabinCard
            Url={
              "/bookings/cabin/" +
              accommodation.id +
              "?accommodationId=" +
              accommodation.id +
              "&accommodationName=" +
              accommodation.accommodationNumber +
              " - " +
              accommodation.name
            }
            ImgSrc={accommodation.imageCarouselUrl}
            Title={
              accommodation.accommodationNumber + ", " + accommodation.name
            }
            Location={location}
            Desc={description}
            BadgeBg={badgeBg}
            BadgeText={badgeText}
            Horizontal={isPrint || isMobile}
            className="h-100"
          />
        </Col>
        <Col xl={9}>
          <div className="react-datepicker-inline  react-datepicker-inline-months">
            <ReactDayPickerWrapper
              selectionMode="range"
              interval={1}
              reservedDates={reservedDates}
              changeMonth={(date) => onMonthChange(date)}
              numberOfMonths={2}
              accommodationId={accommodation.id}
              onRangeSelected={onDatePickerRangeSelected}
              allowRangeSelectOnReservedDates={false}
              showResetRangeButton={false}
              noPastDatesSelectable={true}
              onIcalClick={handleIcalClick}
            />
          </div>

          <Button
            onClick={() => setBookingDetailsTableOpen(!bookingDetailsTableOpen)}
            aria-controls="example-collapse-text"
            aria-expanded={bookingDetailsTableOpen}
            variant="outline-blue"
            className="w-100 mt-16"
          >
            <Icon Name="chevron-expand" /> Näytä varauslista
          </Button>
        </Col>
        <BookingModalWrapper
          accommodationId={accommodation.id}
          accommodationName={accommodation.name}
          accommodationNumber={accommodation.accommodationNumber}
          showModal={bookingModalOpen}
          selectedDateRange={dayPickerSelectedRange}
          setParentShowBookingModal={setBookingModalOpen}
          callParentInitFetch={() => initFetch(currentFetchDate)}
        />
        {bookingDetailsTableOpen && isFullBookingDetailsLoading && (
          <>
            <div className="d-flex gap-16 align-items-center">
              <Spinner animation="border" />
              <span>Ladataan varauksia…</span>
            </div>
          </>
        )}
        {bookingDetailsTableOpen && !isFullBookingDetailsLoading && (
          <Col xs={12}>
            <BookingRowsTable
              bookings={fullBookingDetailsForBookingsTable}
              accommodationId={accommodation.id}
              clearAllPastBookings={true}
              onICalSaved={refetchAfterICalSave}
            />
          </Col>
        )}
      </Row>
      <hr />

      {/* iCal Blocked Period Modal */}
      <ICalEventModal
        show={showICalModal}
        onHide={() => setShowICalModal(false)}
        eventData={selectedICalBooking}
        accommodationId={accommodation.id}
        onSave={refetchAfterICalSave}
      />
    </>
  );
};
export default AccommodationBookings;
